// @ts-check

import React, { useMemo } from "react";

import { InlineStack, Card, Text, BlockStack, Banner, Checkbox } from "@shopify/polaris";
import { BarChart } from "@shopify/polaris-viz";

import { dateRangeTypeToWords } from "components/DateRangePicker/util";
import { SyncingPlaceholder } from "components/SyncingPlaceholder/SyncingPlaceholder";

import { useGlobalContext } from "context/GlobalContext";

export const OrdersBySource = ({
  title,
  loading,
  data: _data,
  error,
}) => {
  const {
    syncing,
    datePeriodType,
    includeNoSourceGraphs,
    setIncludeNoSourceGraphs,
  } = useGlobalContext();

  const formattedData = useMemo(() => {
    const data = _data || [];

    const filteredData = includeNoSourceGraphs ?
      data :
      data.filter((item) => item.name !== "No source");

    return {
      data: filteredData,
    };
  }, [_data, includeNoSourceGraphs]);

  const loadingStats = loading || syncing;

  let content;
  if (!loading && (!_data || error)) {
    content = (
      <Banner
        title="Error loading graph"
        tone="critical"
        action={{
          content: "Contact support",
          onAction: () => window.$crisp.push(["do", "chat:open"]),
        }}
      >
        <BlockStack>
          <Text as="p">There was an error loading this graph.</Text>
        </BlockStack>
      </Banner>
    );
  } else {
    content = (
      <SyncingPlaceholder variant="line-chart">
        <div style={{ height: "300px" }}>
          <BarChart
            theme="Light"
            data={formattedData.data}
            type="stacked"
          />
        </div>
      </SyncingPlaceholder>
    );
  }

  let checkbox;
  if (!loadingStats) {
    checkbox = (
      <Checkbox
        label={<Text variant="bodySm" as="p" tone="subdued">Include no source</Text>}
        checked={includeNoSourceGraphs}
        onChange={(checked) => setIncludeNoSourceGraphs(checked)}
      />
    );
  }

  return (
    <Card>
      <BlockStack gap="800">
        <InlineStack align="space-between">
          <BlockStack>
            <Text as="h2" variant="headingSm" tone="subdued" fontWeight="regular">
              {title}
            </Text>
          </BlockStack>

          <BlockStack>
            <Text variant="bodyMd" as="p" tone="subdued" alignment="end">
              {dateRangeTypeToWords(datePeriodType)}
            </Text>

            {checkbox}
          </BlockStack>
        </InlineStack>

        {content}
      </BlockStack>
    </Card>
  );
};
